import {
  Alert,
  Box,
  Button,
  Theme,
  Typography,
  useTheme
} from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ActivityEnrollableTimesQuery,
  ChildFragmentFragment,
  ClassDetailsQuery_LeaderFragment
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  briefDurationTimesString,
  dayjs,
  formatDateWithWeekday,
  formatDateWithWeekdayLong,
  formatTime
} from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import {
  AvailableDateSelector,
  useEsaAvailability,
  useShowCredits
} from "@outschool/ui-components-website";
import { Select } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import _ from "lodash";
import lodashGroupBy from "lodash/groupBy";
import lodashMap from "lodash/map";
import React from "react";

import AvailableTimesHeader from "../availableTimes/AvailableTimesHeader";
import ChooseAnotherTime from "../availableTimes/ChooseAnotherTime";
import LearnerSwitcher, {
  useGetLearners
} from "../subcomponents/LearnerSwitcher";

export interface ActivityEnrollableTimesProps {
  activity: ActivityEnrollableTimesModal_ActivityFragment;
  leader: ClassDetailsQuery_LeaderFragment;
  onEnrollClick: (args: { startTime: Date; orgTeacherUid?: string }) => void;
}

export default function ActivityEnrollableTimes({
  activity,
  leader,
  onEnrollClick
}: ActivityEnrollableTimesProps) {
  const themeHelper: any = useTheme();
  const { t } = useTranslation(
    "client\\components\\detailPage\\activityEnrollableTimes\\ActivityEnrollableTimes"
  );
  const today = new Date();
  const [teacherSelectedUid, setTeacherSelectedUid] = React.useState<
    string | null
  >(null);
  const [hasSelectedTime, setHasSelectedTime] = React.useState<boolean>(false);
  const trackingLabel = "activity_enrollable_times";
  const trackEvent = useTrackEvent();
  const isSellerOrg = !!leader?.ownerOfSellerOrg?.uid;
  const { isEsaSession, userTimeZone, isLoggedIn } = useSession();
  const showCredits = useShowCredits();
  const {
    filterDatesByEsaBookingWindow,
    shouldBlockEsaPurchase,
    blockedEsaPurchaseMessage
  } = useEsaAvailability(activity);

  const teacherOptions: { value: string | null; label: string }[] =
    React.useMemo(() => {
      if (!isSellerOrg) {
        return [];
      }
      const allTeachers = activity.enrollableTimes.reduce((obj, et) => {
        const { uid, name } = et.teacher;
        if (!!uid && !!name) {
          obj[uid] = name;
        }
        return obj;
      }, {} as Record<string, string>);
      return [
        { value: null, label: t`All teachers` },
        ...Object.keys(allTeachers).map(key => ({
          value: key,
          label: allTeachers[key]
        }))
      ];
    }, [activity.enrollableTimes, isSellerOrg, t]);

  const enrollableTimesFiltered = React.useMemo(() => {
    if (!teacherSelectedUid) {
      // remove duplicate times for org teachers if applicable
      return _.uniqBy(activity.enrollableTimes, "startTime");
    }
    return activity.enrollableTimes.filter(
      et => et.teacher.uid === teacherSelectedUid
    );
  }, [activity.enrollableTimes, teacherSelectedUid]);

  const startOfDateString = React.useCallback(
    (date: Date) => dayjs(date).tz(userTimeZone!).startOf("date").toISOString(),
    [userTimeZone]
  );
  const availableTimesGroupedByDate = React.useMemo(
    () =>
      lodashGroupBy(
        lodashMap(enrollableTimesFiltered, "startTime"),
        startTime => startOfDateString(startTime)
      ),
    [enrollableTimesFiltered, startOfDateString]
  );
  const availableDates = isEsaSession
    ? filterDatesByEsaBookingWindow(Object.keys(availableTimesGroupedByDate))
    : Object.keys(availableTimesGroupedByDate);

  const firstAvailableDate: Date =
    availableDates.length === 0
      ? today
      : dayjs.min(availableDates.map(date => dayjs(date))).toDate() ?? today;
  const [selectedDateTime, setSelectedDateTime] =
    React.useState<Date>(firstAvailableDate);
  const selectedEnrollableTime = enrollableTimesFiltered?.find(
    e => e.startTime === selectedDateTime.toISOString()
  );
  const availableTimesForSelectedDate = availableDates.includes(
    startOfDateString(selectedDateTime)
  )
    ? availableTimesGroupedByDate[startOfDateString(selectedDateTime)] || []
    : [];

  const isMobile = useIsMobile();
  const learners = useGetLearners();
  const [selectedLearner, setSelectedLearner] =
    React.useState<ChildFragmentFragment>(learners[0]);

  const handleDayClick = (date: Date) => {
    setSelectedDateTime(date);
    setHasSelectedTime(false);
    trackEvent(`${trackingLabel}_date_change`, {
      activityUid: activity.uid,
      selectedDate: date
    });
  };

  const handleTimeChange = (date: Date) => {
    setSelectedDateTime(date);
    setHasSelectedTime(true);
    trackEvent(`${trackingLabel}_time_change`, {
      activityUid: activity.uid,
      selectedTime: date
    });
  };

  const handleOnEnrollClick = React.useCallback(() => {
    if (!hasSelectedTime) {
      return;
    }
    const orgTeacherUid =
      teacherOptions?.length > 1
        ? selectedEnrollableTime?.teacher.uid
        : undefined;
    onEnrollClick({
      startTime: dayjs(selectedEnrollableTime?.startTime).toDate(),
      orgTeacherUid
    });
    trackEvent(`${trackingLabel}_enroll`, {
      activityUid: activity.uid,
      selectedDate: selectedEnrollableTime?.startTime
    });
  }, [
    activity,
    onEnrollClick,
    selectedEnrollableTime,
    teacherOptions,
    trackEvent,
    hasSelectedTime
  ]);

  if (shouldBlockEsaPurchase) {
    return (
      <>
        <AvailableTimesHeader userTimeZone={userTimeZone!} />
        <Alert severity="warning" sx={{ marginBottom: "1em" }}>
          <Typography variant="body2">{blockedEsaPurchaseMessage}</Typography>
        </Alert>
      </>
    );
  }

  return (
    <>
      <AvailableTimesHeader userTimeZone={userTimeZone!} />
      <Box
        sx={{
          overflow: "hidden"
        }}
      >
        {isMobile && (
          <LearnerSwitcher
            learners={learners}
            selectedLearner={selectedLearner}
            setSelectedLearner={setSelectedLearner}
          />
        )}
        <Box sx={{ paddingY: "0.5em" }}>
          {teacherOptions.length > 1 && (
            <Select
              handleChangeOnBlur={false}
              onChange={setTeacherSelectedUid}
              options={teacherOptions}
              value={teacherSelectedUid}
              sx={{
                width: "100%",
                border: "1px solid",
                borderColor: themeHelper.palette.grey[300],
                color: themeHelper.palette.grey[900]
              }}
            />
          )}
        </Box>
        <Box
          sx={(theme: Theme) => ({
            backgroundColor: "common.white",
            [theme.breakpoints.up("md")]: {
              paddingY: "1em"
            }
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              display: "grid",
              gridTemplateColumns: "auto",
              gridTemplateAreas: '"date-selector" "time-selector"',
              [theme.breakpoints.up("md")]: {
                gridTemplateColumns: "min-content auto",
                gridTemplateAreas: '"date-selector time-selector"',
                gap: 32
              }
            })}
          >
            <AvailableDateSelector
              availableDates={availableDates}
              today={today}
              firstAvailableDate={firstAvailableDate}
              selectedDate={selectedDateTime}
              timeZone={userTimeZone!}
              startOfDateString={startOfDateString}
              handleDayClick={handleDayClick}
            />
            <AvailableTimesGrid
              availableTimes={availableTimesForSelectedDate}
              selectedDateTime={selectedDateTime}
              hasSelectedTime={hasSelectedTime}
              timeZone={userTimeZone!}
              handleTimeChange={handleTimeChange}
            />
          </Box>
        </Box>
        {isMobile ? (
          <Box>
            <Box
              flex
              sx={{
                paddingBottom: "3.5em",
                justifyContent: "center"
              }}
            >
              <ChooseAnotherTime
                activity={activity}
                leader={leader}
                showDescription={false}
              />
            </Box>
            <AvailableTimesFooterMobile
              hasSelectedTime={hasSelectedTime}
              continueButtonText={
                !isLoggedIn && showCredits ? t`Join` : t`Enroll`
              }
              handleContinueClick={handleOnEnrollClick}
            />
          </Box>
        ) : (
          <>
            <AvailableTimesFooter
              selectedDateTime={selectedDateTime}
              selectedEnrollableTime={selectedEnrollableTime}
              timeZone={userTimeZone!}
              hasSelectedTime={hasSelectedTime}
              continueButtonText={
                !isLoggedIn && showCredits ? t`Join` : t`Enroll`
              }
              handleContinueClick={handleOnEnrollClick}
              showTeacher={teacherOptions?.length > 1}
            />
            <ChooseAnotherTime activity={activity} leader={leader} />
          </>
        )}
      </Box>
    </>
  );
}

export function AvailableTimesGrid({
  availableTimes,
  selectedDateTime,
  hasSelectedTime,
  timeZone,
  handleTimeChange,
  maxHeight
}: {
  availableTimes: string[];
  selectedDateTime: Date | undefined;
  hasSelectedTime: boolean;
  timeZone: string;
  handleTimeChange: (date: Date) => void;
  maxHeight?: number;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\activityEnrollableTimes\\ActivityEnrollableTimes"
  );
  const isSelected = (time: string) =>
    hasSelectedTime && dayjs(selectedDateTime).isSame(time);

  const selectedStyles = {
    borderColor: "primary.700",
    backgroundColor: "primary.700",
    color: "white"
  };

  return (
    <Box
      sx={{
        position: "relative"
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "calc(100% - 15px)",
          height: "100%",
          background:
            "linear-gradient(to bottom, transparent calc(100% - 4em), white 100%)",
          pointerEvents: "none"
        }}
      />
      <Box
        sx={(theme: Theme) => ({
          marginTop: 32,
          gridArea: "time-selector",
          flexGrow: 1,
          overflowX: "hidden",
          padding: 0,
          [theme.breakpoints.up("md")]: {
            marginY: 0,
            overflowY: "auto",
            maxHeight: maxHeight ?? 320
          }
        })}
      >
        <Typography
          component="h6"
          sx={{
            marginBottom: "1em",
            fontWeight: "500"
          }}
        >
          {formatDateWithWeekday(selectedDateTime, timeZone)}
        </Typography>
        {availableTimes?.length === 0 ? (
          <Box sx={{ paddingBottom: "3em" }}>
            <Typography>{t`No available times for the selected date.`}</Typography>
          </Box>
        ) : (
          <Box
            flex
            sx={{
              flexWrap: "wrap",
              gap: "8px",
              paddingBottom: "3em",
              minWidth: 210
            }}
          >
            {availableTimes.map(time => (
              <label style={{ margin: 0 }} key={time} htmlFor={time}>
                <Box
                  sx={(theme: Theme) => ({
                    paddingX: 12,
                    paddingY: 6,
                    minWidth: "120px",
                    textWrap: "nowrap",
                    border: "2px solid",
                    borderRadius: "999px",
                    borderColor: "neutral.200",
                    textAlign: "center",
                    fontWeight: 500,
                    "&:hover": !isSelected(time)
                      ? {
                          borderColor: "primary.800",
                          backgroundColor: "neutral.100",
                          color: "neutral.900",
                          cursor: "pointer"
                        }
                      : {},
                    "&:focus-within": !isSelected(time)
                      ? {
                          borderColor: "primary.800",
                          backgroundColor: "neutral.100",
                          color: "neutral.900",
                          outlineOffset: "2px",
                          outline: "2px solid",
                          outlineColor: "primary.800"
                        }
                      : {},
                    ...(isSelected(time) && selectedStyles),
                    [theme.breakpoints.down("md")]: {
                      minWidth: "90px"
                    }
                  })}
                  onClick={() => {
                    handleTimeChange(new Date(time));
                  }}
                >
                  <Typography
                    sx={(theme: Theme) => ({
                      fontWeight: 500,
                      fontSize: theme.typography.pxToRem(16)
                    })}
                  >
                    {formatTime(time, timeZone, false, true)}
                  </Typography>
                </Box>
              </label>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function AvailableTimesFooter({
  selectedDateTime,
  timeZone,
  hasSelectedTime,
  continueButtonText,
  handleContinueClick,
  showTeacher,
  selectedEnrollableTime
}: {
  selectedDateTime: Date;
  timeZone: string;
  hasSelectedTime: boolean;
  continueButtonText: string;
  handleContinueClick: () => void;
  showTeacher: boolean;
  selectedEnrollableTime?: ActivityEnrollableTimesQuery["activity"]["enrollableTimes"][number];
}) {
  return (
    <Box
      flex
      sx={{
        paddingY: "1em",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1.5em",
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
        borderTop: "1px solid",
        borderTopColor: "neutral.200"
      }}
    >
      <Box>
        {showTeacher && (
          <Typography variant="body1">
            {selectedEnrollableTime?.teacher?.name}
          </Typography>
        )}
        {hasSelectedTime && (
          <Box
            flex
            sx={{
              flexDirection: "row",
              flexWrap: "wrap",
              columnGap: "1em"
            }}
          >
            <Typography variant="body1">
              {formatDateWithWeekdayLong(selectedDateTime, timeZone, true)}
            </Typography>
            <Typography variant="body1">
              {briefDurationTimesString(
                selectedDateTime,
                selectedEnrollableTime?.endTime,
                timeZone,
                false
              )}
            </Typography>
          </Box>
        )}
      </Box>
      <Button
        variant="contained"
        sx={{
          marginLeft: "1em"
        }}
        onClick={handleContinueClick}
        disabled={!hasSelectedTime}
      >
        {continueButtonText}
      </Button>
    </Box>
  );
}

export function AvailableTimesFooterMobile({
  hasSelectedTime,
  continueButtonText,
  handleContinueClick
}: {
  hasSelectedTime: boolean;
  continueButtonText: string;
  handleContinueClick: () => void;
}) {
  return (
    <Box
      flex
      sx={{
        position: "fixed",
        bottom: 0,
        paddingY: "1em",
        paddingLeft: "1em",
        paddingRight: "1em",
        width: "100%",
        zIndex: 10,
        gap: "0.5em",
        left: "50%",
        transform: "translateX(-50%)",
        background:
          "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))"
      }}
    >
      <Button
        variant="contained"
        sx={{
          width: "100%"
        }}
        onClick={handleContinueClick}
        disabled={!hasSelectedTime}
      >
        {continueButtonText}
      </Button>
    </Box>
  );
}
